<template>
  <section id="widget-grid">
    <div class="md-layout md-gutter">
      <div class="md-layout-item md-size-55 md-small-size-100">
        <client-personal-data :person="person" v-if="isLoaded" />
        <addresses :person-id="person.id" v-if="person !== null" />
        <customer-onboarding
          :person-id="person.id"
          :initial-answers="person.onboardingJson"
          v-if="person !== null"
          @updated="onboardingUpdated"
        />
        <customer-documents :person-id="person.id" v-if="person !== null" />
        <sms-history :person-id="personId" person-name="System" />
      </div>
      <div
        class="md-layout-item md-size-45 md-small-size-100"
        v-if="isLoaded && $can('payments')"
      >
        <payment-flow />
        <payment-detail />
      </div>
      <div class="md-layout-item md-size-100" v-if="$can('transactions')">
        <transactions :personId="personId" />
      </div>
      <div class="md-layout-item md-size-50 md-small-size-100">
        <div
          class="client-detail-card"
          v-if="person !== null && $can('appliances')"
        >
          <deferred-payments :person-id="person.id" :person="person" />
        </div>
        <div class="client-detail-card" v-if="$can('tickets')">
          <ticket :personId="personId" />
        </div>
      </div>
      <div class="md-layout-item md-size-50 md-small-size-100">
        <div class="client-detail-card">
          <devices :devices="devices" v-if="isLoaded" />
        </div>
        <div class="client-detail-card">
          <widget :title="$tc('words.devices')" id="client-map">
            <client-map
              :mappingService="mappingService"
              ref="clientMapRef"
              :edit="true"
              @locationEdited="deviceLocationsEditedSet"
              :zoom="5"
            />
          </widget>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { geoJsonToLatLon } from "@/Helpers/Utils.js"
import { notify } from "@/mixins/notify.js"
import { timing } from "@/mixins/timing.js"
import Addresses from "@/modules/Client/Addresses.vue"
import ClientPersonalData from "@/modules/Client/ClientPersonalData.vue"
import CustomerDocuments from "@/modules/Client/CustomerDocuments.vue"
import CustomerOnboarding from "@/modules/Client/CustomerOnboarding.vue"
import DeferredPayments from "@/modules/Client/DeferredPayments.vue"
import Devices from "@/modules/Client/Devices.vue"
import PaymentDetail from "@/modules/Client/PaymentDetail.vue"
import PaymentFlow from "@/modules/Client/PaymentFlow.vue"
import SmsHistory from "@/modules/Client/SmsHistory.vue"
import Ticket from "@/modules/Client/Ticket.vue"
import Transactions from "@/modules/Client/Transactions.vue"
import ClientMap from "@/modules/Map/ClientMap.vue"
import { DeviceAddressService } from "@/services/DeviceAddressService.js"
import { MappingService, MARKER_TYPE } from "@/services/MappingService.js"
import { PersonService } from "@/services/PersonService.js"
import { EventBus } from "@/shared/eventbus.js"
import Widget from "@/shared/Widget.vue"

export default {
  name: "Client",
  mixins: [notify, timing],
  components: {
    DeferredPayments,
    ClientPersonalData,
    CustomerDocuments,
    CustomerOnboarding,
    SmsHistory,
    PaymentFlow,
    Transactions,
    PaymentDetail,
    Ticket,
    Addresses,
    ClientMap,
    Devices,
    Widget,
  },
  data() {
    return {
      personService: new PersonService(),
      mappingService: new MappingService(),
      deviceAddressService: new DeviceAddressService(),
      personId: null,
      isLoaded: false,
      editPerson: false,
      person: null,
      devices: [],
    }
  },
  created() {
    this.personId = this.$route.params.id
    this.getDetails(this.personId)
  },
  destroyed() {
    this.$store.state.person = null
    this.$store.state.devices = null
  },
  mounted() {
    EventBus.$on("setMapCenterForDevice", (device) => {
      const location = geoJsonToLatLon(device.geo)
      if (location == null) {
        this.alertNotify("warn", "Device has no location")
        return
      }
      this.$refs.clientMapRef.focusOnItem([location.lat, location.lon])
    })
  },
  methods: {
    async getDetails(id) {
      try {
        this.person = await this.personService.getPerson(id)
        this.isLoaded = true
        this.$store.state.person = this.person
        this.$store.state.devices = this.person.devices
        this.devices = this.person.devices
        this.setClientMapData()
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    onboardingUpdated(answers) {
      this.person.onboardingJson = answers
    },
    async deviceLocationsEditedSet(editedItems) {
      try {
        await this.deviceAddressService.updateDeviceAddresses(editedItems)
        this.alertNotify("success", "Device locations updated successfully!")
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    setClientMapData() {
      const markingInfos = []
      this.devices.map((device) => {
        const location = geoJsonToLatLon(device.geo)
        if (location == null) {
          return
        }
        const lat = location.lat
        const lon = location.lon
        let markerType = ""
        switch (device.device_type) {
          case "e_bike":
            markerType = MARKER_TYPE.E_BIKE
            break
          case "solar_home_system":
            markerType = MARKER_TYPE.SHS
            break
          default:
            markerType = MARKER_TYPE.METER
        }
        markingInfos.push({
          id: device.id,
          name: device.name,
          serialNumber: device.device_serial,
          lat: lat,
          lon: lon,
          deviceType: device.device_type,
          markerType: markerType,
        })
        this.mappingService.setCenter([lat, lon])
      })
      this.mappingService.setMarkingInfos(markingInfos)
      this.$refs.clientMapRef.setDeviceMarkers()
    },
  },
}
</script>
<style scoped lang="scss">
[data-letters]:before {
  content: attr(data-letters);
  display: inline-block;
  font-size: 1em;
  width: 2.5em;
  height: 2.5em;
  line-height: 2.5em;
  text-align: center;
  border-radius: 50%;
  background: plum;
  vertical-align: middle;
  margin-right: 1em;
  color: white;
}
</style>
